import {
  type BridgeCard,
  type BridgeSeat,
  type BridgeSuit,
  CRAZY_EIGHTS_TARGET,
  type CrazyEightsGame,
  type CrazyEightsResult,
  type CrazyEightsRobotLevel,
  type CrazyEightsTotals,
  SINGLE_PLAYER,
  addHand,
  crazyEightsChoose,
  crazyEightsDraw,
  crazyEightsGameWinner,
  crazyEightsLegalPlays,
  crazyEightsPass,
  crazyEightsPlay,
  crazyEightsResult,
  crazyEightsViewFor,
  isWild,
  newCrazyEightsGame,
  seededRandom,
} from '@strategy4kids/game-engine';
import { create } from 'zustand';
import { seatPan } from '@strategy4kids/audio';
import type { AppQuery } from '../lib/query.js';
import { playSfx, setAmbientMood } from '../services/audio.js';
import { cueDeal, cueRoundOrGame, cueTurn } from './sfx-cues.js';
import { chatGameEnd, chatRound, chatterNewGame, robotsOf } from './table-chatter.js';

/**
 * The Crazy Eights store (single device: you in South, robots in the other seats). Its own store, like Bridge's and
 * Whist's, because nothing here is FEN-shaped. Robots decide from `crazyEightsViewFor` only.
 *
 * A game is a run of hands; the winner of a hand scores the others' card points and the first to 100 wins the game.
 * Points are only a score: nothing is staked.
 */
export const HUMAN_SEAT: BridgeSeat = 'S';
export type PlayerCount = 2 | 3 | 4;
export const TABLE_SEATS: Readonly<Record<PlayerCount, readonly BridgeSeat[]>> = {
  2: ['N', 'S'],
  3: ['N', 'S', 'W'],
  4: ['N', 'E', 'S', 'W'],
};

/** Pause before a robot acts so a kid can follow the pile. Zero in tests and with `?engine=fake`. */
const ROBOT_DELAY_MS = 650;

export interface CrazyEightsState {
  game: CrazyEightsGame | null;
  result: CrazyEightsResult | null;
  /** Hands dealt so far in this game, counting the current one (0 = no game). */
  handNumber: number;
  totals: CrazyEightsTotals;
  gameWinner: BridgeSeat | null;
  players: PlayerCount;
  strength: CrazyEightsRobotLevel;
  fourColour: boolean;
  /** Add the red and the black Joker to the deck (wild, like the eights). Changing it deals a fresh game. */
  jokers: boolean;
  seed: number | null;
  query: AppQuery | null;
  /** A wild card (an eight or a Joker) the human has chosen to play and now has to name a suit for. */
  pendingEight: BridgeCard | null;
  start(query: AppQuery, seed?: number): void;
  nextHand(): void;
  newGame(): void;
  stop(): void;
  /** Tap a card: a wild card (an eight or a Joker) asks for a suit first; any other legal card is played. */
  tap(card: BridgeCard): void;
  chooseSuit(suit: BridgeSuit): void;
  cancelEight(): void;
  draw(): void;
  pass(): void;
  setStrength(strength: CrazyEightsRobotLevel): void;
  setFourColour(on: boolean): void;
  setJokers(on: boolean): void;
  setPlayers(players: PlayerCount): void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);
/** Each hand of a game has its own seed derived from the game's, so a seeded game replays exactly. */
export const handSeed = (seed: number, handIndex: number): number => (seed + Math.imul(handIndex, 7919)) >>> 0;

export const useCrazyEightsStore = create<CrazyEightsState>((set, get) => {
  /** Take a new game state; when it ends the hand, score it, and when that ends the game, say so. */
  const advance = (game: CrazyEightsGame) => {
    const before = get().game;
    const result = crazyEightsResult(game);
    if (!result) {
      set({ game });
      cueTurn(before && before.phase === 'play' ? before.turn : null, game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
      return;
    }
    const totals = addHand(get().totals, result);
    const winner = crazyEightsGameWinner(totals);
    set({ game, result, totals, gameWinner: winner, pendingEight: null });
    // The game-ending hand gets win/lose; any other hand the stinger.
    cueRoundOrGame((winner ?? result.winner) === HUMAN_SEAT ? 'won' : 'lost', winner !== null);
    // Table talk (public: who went out, who won the game).
    const robots = robotsOf(TABLE_SEATS[get().players]);
    if (winner) chatGameEnd([winner], robots);
    else chatRound(robots.includes(result.winner) ? result.winner : null, robots.find((r) => r !== result.winner) ?? null);
  };

  /** If a robot is to act, let it, after a short pause; stops at the human's turn or the end of the hand. */
  const driveRobots = () => {
    clearTimer();
    const { game, query, strength } = get();
    if (!game || game.phase === 'done' || game.controllers[game.turn] !== 'ai') return;
    const delay = query?.engine === 'fake' ? 0 : ROBOT_DELAY_MS;
    timer = setTimeout(() => {
      const current = get().game;
      if (!current || current.phase === 'done' || current.controllers[current.turn] !== 'ai') return;
      const random = seededRandom(current.seed + current.discard.length * 31 + current.stock.length);
      const action = crazyEightsChoose(crazyEightsViewFor(current, current.turn), strength, random);
      if (action.type === 'play') {
        // The card is heard from the seat that played it (spec P2: seat panning).
        playSfx('card-play', seatPan(current.turn));
        advance(crazyEightsPlay(current, action.card, action.suit));
      } else if (action.type === 'draw') advance(crazyEightsDraw(current));
      else advance(crazyEightsPass(current));
      driveRobots();
    }, delay);
  };

  const deal = (handIndex: number) => {
    clearTimer();
    const { seed, players, jokers } = get();
    if (seed === null) return;
    const seats = TABLE_SEATS[players];
    const game = newCrazyEightsGame({ seed: handSeed(seed, handIndex), seats: [...seats], dealer: seats[handIndex % seats.length], controllers: SINGLE_PLAYER, jokers });
    set({ game, result: null, handNumber: handIndex + 1, pendingEight: null });
    cueDeal();
    cueTurn(null, game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
    driveRobots();
  };

  const begin = (query: AppQuery, seed: number) => {
    clearTimer();
    chatterNewGame();
    set({ seed, query, totals: {}, gameWinner: null, result: null, handNumber: 0, pendingEight: null });
    deal(0);
  };

  const humanTurn = (g: CrazyEightsGame | null): g is CrazyEightsGame => !!g && g.phase === 'play' && g.turn === HUMAN_SEAT;

  return {
    game: null,
    result: null,
    handNumber: 0,
    totals: {},
    gameWinner: null,
    players: 4,
    strength: 'easy',
    fourColour: false,
    jokers: false,
    seed: null,
    query: null,
    pendingEight: null,
    start: (query, seed) => {
      setAmbientMood('cards');
      return begin(query, seed ?? query.deal ?? freshSeed());
    },
    nextHand: () => {
      const { result, gameWinner: winner, handNumber } = get();
      if (!result || winner) return;
      deal(handNumber);
    },
    newGame: () => {
      const { query } = get();
      if (query) begin(query, query.deal ?? freshSeed());
    },
    stop: () => {
      clearTimer();
      set({ game: null, result: null, seed: null, handNumber: 0, totals: {}, gameWinner: null, pendingEight: null });
    },
    tap: (card) => {
      const { game } = get();
      if (!humanTurn(game) || !crazyEightsLegalPlays(game).some((c) => c.suit === card.suit && c.rank === card.rank)) return;
      if (isWild(card)) {
        set({ pendingEight: card });
        return;
      }
      // Only a card that reaches the table is heard; an illegal tap is silent.
      playSfx('card-play', seatPan(HUMAN_SEAT));
      advance(crazyEightsPlay(game, card));
      driveRobots();
    },
    chooseSuit: (suit) => {
      const { game, pendingEight } = get();
      if (!humanTurn(game) || !pendingEight) return;
      set({ pendingEight: null });
      // The wild card reaches the table now, so this is where it is heard.
      playSfx('card-play', seatPan(HUMAN_SEAT));
      advance(crazyEightsPlay(game, pendingEight, suit));
      driveRobots();
    },
    cancelEight: () => set({ pendingEight: null }),
    draw: () => {
      const { game } = get();
      if (!humanTurn(game)) return;
      if (!crazyEightsViewFor(game, HUMAN_SEAT).canDraw) return;
      advance(crazyEightsDraw(game));
      driveRobots();
    },
    pass: () => {
      const { game } = get();
      if (!humanTurn(game) || !game.drew) return;
      advance(crazyEightsPass(game));
      driveRobots();
    },
    setStrength: (strength) => set({ strength }),
    setFourColour: (fourColour) => set({ fourColour }),
    setJokers: (jokers) => {
      set({ jokers });
      const { query } = get();
      if (query && get().seed !== null) begin(query, get().seed as number);
    },
    setPlayers: (players) => {
      set({ players });
      const { query } = get();
      if (query && get().seed !== null) begin(query, get().seed as number);
    },
  };
});

export { CRAZY_EIGHTS_TARGET };
